import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import { Mail } from 'lucide-react';
import { login } from '../../api/auth';
import { getErrorMessage } from '../../api/errors';
import { useSession } from '../../state/session';
import { Alert, Button, Field, Input } from '../../ui';
import { PasswordInput } from '../common/PasswordInput';
import styles from './auth.module.css';

const schema = Yup.object({
  email: Yup.string().trim().email('Enter a valid email address').required('Email is required'),
  password: Yup.string().required('Password is required'),
});

export function LoginForm({ onSuccess, onForgotPassword, defaultEmail = '', lockEmail = false }) {
  const { signIn } = useSession();
  const mutation = useMutation({
    mutationFn: login,
    onSuccess: (user) => {
      signIn(user);
      onSuccess?.(user);
    },
  });

  const formik = useFormik({
    initialValues: { email: defaultEmail, password: '' },
    validationSchema: schema,
    onSubmit: (values) => mutation.mutate({ email: values.email.trim(), password: values.password }),
  });

  const err = (name) => (formik.touched[name] && formik.errors[name]) || undefined;

  return (
    <form className={styles.form} onSubmit={formik.handleSubmit} noValidate>
      {mutation.isError && <Alert tone="danger">{getErrorMessage(mutation.error)}</Alert>}
      <Field label="Email" error={err('email')}>
        <Input
          type="email"
          autoComplete="email"
          inputMode="email"
          leading={<Mail />}
          readOnly={lockEmail}
          {...formik.getFieldProps('email')}
        />
      </Field>
      <Field
        label="Password"
        error={err('password')}
        action={
          onForgotPassword && (
            <button type="button" className={styles.inlineLink} onClick={onForgotPassword}>
              Forgot password?
            </button>
          )
        }
      >
        <PasswordInput autoComplete="current-password" {...formik.getFieldProps('password')} />
      </Field>
      <Button type="submit" size="lg" block loading={mutation.isPending}>
        Sign in
      </Button>
    </form>
  );
}
